import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { proofUrl, registrationPack } from '../lib/api';
import { dateOnly, formatPhone, friendlyError, proofKindLabel, registrationChannelLabel, registrationStatusLabel, sourceLabel } from '../lib/format';
import { describeEvent } from '../lib/timeline';
import type { LeadEvent, RegistrationPack } from '../lib/types';
import { Button, ErrorText } from '../components/ui';

// A printable record of one builder registration, for a dispute with the builder or another partner.
export function ProofPack() {
  const { id } = useParams();
  const { members } = useAuth();
  const [pack, setPack] = useState<RegistrationPack | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { if (id) registrationPack(id).then(setPack).catch((e) => setError(friendlyError(e))); }, [id]);
  if (error) return <div className="p-6"><ErrorText>{error}</ErrorText></div>;
  if (!pack) return <p className="p-6 text-muted">Loading…</p>;
  const r = pack.registration;
  const byName = (n: string | null) => n ?? 'ReAstra';

  return (
    <div className="mx-auto max-w-[800px] bg-surface px-6 py-8 text-sm print:max-w-none print:px-0 print:py-0">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted">Client registration record</p>
          <h1 className="font-display text-2xl">{pack.buyer.name ?? 'Client'} · {pack.project.name}</h1>
          <p className="text-muted">{pack.firm.name}{pack.firm.rera_number ? ` · RERA agent ${pack.firm.rera_number}` : ''}</p>
        </div>
        <Button variant="primary" className="print:hidden" onClick={() => window.print()}>Print or save as PDF</Button>
      </div>
      {pack.lead.is_test && <p className="mb-4 rounded-lg border border-warn bg-warnSoft px-3 py-2 text-warn">Test data from the simulator. Not a real buyer.</p>}

      <Block title="Summary">
        <Rows rows={[
          ['Client', `${pack.buyer.name ?? '(name not recorded)'} · ${formatPhone(pack.buyer.phone)}`],
          ['Project', `${pack.project.name}${pack.project.builder ? ` by ${pack.project.builder}` : ''}${pack.project.rera_number ? ` · RERA ${pack.project.rera_number}` : ''}`],
          ['Status', registrationStatusLabel[r.status] + (r.closed_reason ? ` · ${r.closed_reason}` : '')],
          ['Registered', `${dateTime(r.sent_at)} by ${registrationChannelLabel[r.sent_via]}${r.sent_to ? ` to ${r.sent_to}` : ''}`],
          ['Acknowledged', r.acknowledged_at ? `${dateTime(r.acknowledged_at)}${r.builder_ref ? ` · builder ref ${r.builder_ref}` : ''}` : 'Not yet'],
          ['Valid until', `${dateOnly(r.valid_until)} (${r.valid_days} days from ${r.acknowledged_at ? 'acknowledgement' : 'sending'})`],
          ['Recorded in ReAstra', `${dateTime(r.recorded_at)} by ${r.recorded_by}`],
        ]} />
      </Block>

      <Block title="How the client reached us">
        <Rows rows={[
          ['Our reference', `L-${pack.lead.ref_no}`],
          ['First enquiry', dateTime(pack.lead.created_at)],
          ['Source', [sourceLabel[pack.lead.source] ?? pack.lead.source, pack.lead.source_detail, pack.lead.source_code && `code ${pack.lead.source_code}`].filter(Boolean).join(' · ')],
          ['Relationship manager', pack.lead.owner ?? '—'],
          ...(pack.first_message ? [['First message from the client', `${dateTime(pack.first_message.at)}: "${pack.first_message.text}"`] as [string, string]] : []),
        ]} />
      </Block>

      <Block title="Registration message sent">
        <pre className="whitespace-pre-wrap rounded-lg border border-line bg-surface2 p-3 font-sans">{r.body}</pre>
      </Block>

      <Block title="Site visits">
        {pack.visits.length === 0 ? <p className="text-muted">None recorded.</p> : (
          <ul>{pack.visits.map((v, i) => <li key={i}>{dateTime(v.at)} · {v.status.replace('_', '-')}{v.outcome ? ` · ${v.outcome}` : ''}</li>)}</ul>
        )}
      </Block>

      <Block title="Proof on file">
        {pack.proofs.length === 0 ? <p className="text-muted">No files added yet.</p> : (
          <ul className="flex flex-col gap-1">
            {pack.proofs.map((p, i) => (
              <li key={i}>
                <button className="text-accent hover:underline print:text-ink print:no-underline" onClick={async () => window.open(await proofUrl(p.file_path), '_blank', 'noopener')}>
                  {proofKindLabel[p.kind]}
                </button>
                {' '}· added {dateTime(p.added_at)} by {p.added_by}{p.note ? ` · ${p.note}` : ''}
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block title="Timeline">
        <ol className="flex flex-col gap-1">
          {pack.timeline.map((t, i) => {
            const d = describeEvent({ id: i, kind: t.kind, body: t.body, created_at: t.at } as unknown as LeadEvent, (mid) => members.find((m) => m.id === mid)?.full_name ?? 'a former member');
            return <li key={i}><span className="text-muted">{dateTime(t.at)}</span> · {d.title}{d.detail ? ` · ${d.detail}` : ''} <span className="text-muted">({byName(t.by)})</span></li>;
          })}
        </ol>
      </Block>

      <p className="mt-8 border-t border-line pt-3 text-xs text-muted">
        Generated {dateTime(pack.generated_at)} by {pack.generated_by} from records kept in ReAstra. Each entry was recorded at the time shown; ReAstra does not allow these entries or the proof files to be edited or deleted.
      </p>
    </div>
  );
}

// Disputes need the year and the exact minute, in IST.
function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }) + ' IST';
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return <section className="mb-5 break-inside-avoid"><h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">{title}</h2>{children}</section>;
}
function Rows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="grid grid-cols-[180px_minmax(0,1fr)] gap-x-4 gap-y-1">
      {rows.map(([k, v]) => <Fragment key={k}><dt className="text-muted">{k}</dt><dd>{v}</dd></Fragment>)}
    </dl>
  );
}
